<template>
  <div class="rcs-step rcs-step--basic-info">
    <h2 class="rcs-step__heading">
      {{ t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.title') }}
    </h2>
    <p class="rcs-step__lead">
      {{ t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.subtitle') }}
    </p>

    <div class="rcs-form-grid">
      <div class="rcs-form-grid__field rcs-form-grid__field--full">
        <label for="rcs-brand-name">
          {{ t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.brandDisplayName') }}
        </label>
        <p class="rcs-form-grid__description">
          {{ t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.brandDescription') }}
        </p>
        <Dropdown
          v-if="hasReusableBrands"
          id="rcs-brand-select"
          v-model="brandSelection"
          :options="brandOptions"
          option-label="label"
          option-value="value"
          class="mb-2"
        />
        <InputText
          v-if="isCreatingNewBrand"
          id="rcs-brand-name"
          v-model="form.basic_info.brand_display_name"
          maxlength="100"
          :placeholder="t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.brandPlaceholder')"
        />
        <small class="rcs-form-grid__hint">
          {{ t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.brandHint') }}
        </small>
      </div>

      <div class="rcs-form-grid__field">
        <label for="rcs-agent-name">
          {{ t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.agentDisplayName') }}
        </label>
        <p class="rcs-form-grid__description">
          {{ t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.agentDescription') }}
        </p>
        <InputText
          id="rcs-agent-name"
          v-model="form.basic_info.agent_display_name"
          maxlength="100"
          :placeholder="t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.agentPlaceholder')"
        />
        <small class="rcs-form-grid__hint">
          {{ t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.agentHint') }}
        </small>
      </div>

      <div class="rcs-form-grid__field">
        <label for="rcs-sender-id">
          {{ t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.senderId') }}
        </label>
        <p class="rcs-form-grid__description">
          {{ t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.senderIdDescription') }}
        </p>
        <InputText
          id="rcs-sender-id"
          v-model="form.basic_info.sender_id"
          maxlength="50"
          placeholder="myshop_rcs"
          @input="handleSenderIdManualInput"
        />
        <small
          v-if="senderIdError"
          class="rcs-form-grid__error"
        >
          {{ t(`storeParameters.services.rcsServiceParameter.errors.${senderIdError}`) }}
        </small>
        <small
          v-else
          class="rcs-form-grid__hint"
        >
          {{ t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.senderIdHint') }}
        </small>
      </div>

      <div class="rcs-form-grid__field rcs-form-grid__field--full">
        <label for="rcs-hosting-region">
          {{ t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.hostingRegion') }}
        </label>
        <p class="rcs-form-grid__description">
          {{ t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.hostingRegionDescription') }}
        </p>
        <Dropdown
          id="rcs-hosting-region"
          v-model="form.basic_info.hosting_region"
          :options="hostingRegionOptions"
          option-label="label"
          option-value="value"
          :placeholder="t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.hostingRegionPlaceholder')"
        />
      </div>

    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import {
  RCS_AGENT_PURPOSE_DEFAULT,
  RCS_BILLING_CATEGORY_DEFAULT,
  RCS_HOSTING_REGION_DEFAULT,
  RCS_HOSTING_REGIONS,
} from '@/types/rcs-onboarding';
import type { RcsBrand, RcsDedicatedFormState } from '@/types/rcs-onboarding';

const SENDER_ID_REGEX = /^[A-Za-z0-9_-]{2,50}$/;
/** Sentinel dropdown value meaning "create a brand new brand". */
const NEW_BRAND_VALUE = '__new_brand__';
const buildSenderIdFromAgentName = (value: string): string => value
  .toLowerCase()
  // Convert any non-alphanumeric character to underscore.
  .replace(/[^a-z0-9]/g, '_')
  // Collapse repeating underscores.
  .replace(/_+/g, '_')
  // Remove underscores at both ends.
  .replace(/^_+|_+$/g, '')
  .slice(0, 50);

export default defineComponent({
  name: 'RcsBasicInfoStep',

  components: { InputText, Dropdown },

  props: {
    form: {
      type: Object as PropType<RcsDedicatedFormState>,
      required: true,
    },
    reusableBrands: {
      type: Array as PropType<RcsBrand[]>,
      default: () => [],
    },
  },

  setup(props) {
    const { t } = useI18n();

    const hasReusableBrands = computed(() => props.reusableBrands.length > 0);
    // Pre-select the agent's existing brand when it matches a reusable one,
    // otherwise default to creating a new brand.
    const brandSelection = ref<string>(
      props.form.basic_info.brand_id
        && props.reusableBrands.some((b) => b.id === props.form.basic_info.brand_id)
        ? props.form.basic_info.brand_id
        : NEW_BRAND_VALUE,
    );
    const isCreatingNewBrand = computed(
      () => !hasReusableBrands.value || brandSelection.value === NEW_BRAND_VALUE,
    );
    const brandOptions = computed(() => [
      ...props.reusableBrands.map((b) => ({ label: b.display_name, value: b.id })),
      {
        label: t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.createNewBrand'),
        value: NEW_BRAND_VALUE,
      },
    ]);

    watch(brandSelection, (value) => {
      if (value === NEW_BRAND_VALUE) {
        props.form.basic_info.brand_id = '';
        props.form.basic_info.brand_display_name = '';
        return;
      }
      const brand = props.reusableBrands.find((b) => b.id === value);
      props.form.basic_info.brand_id = value;
      props.form.basic_info.brand_display_name = brand?.display_name || '';
    });

    props.form.basic_info.agent_purpose = RCS_AGENT_PURPOSE_DEFAULT;
    props.form.basic_info.billing_category = RCS_BILLING_CATEGORY_DEFAULT;
    if (!props.form.basic_info.hosting_region) {
      props.form.basic_info.hosting_region = RCS_HOSTING_REGION_DEFAULT;
    }

    const hostingRegionOptions = computed(() => RCS_HOSTING_REGIONS.map((value) => ({
      value,
      label: t(`storeParameters.services.rcsServiceParameter.wizard.basicInfo.hostingRegions.${value}`),
    })));

    const generatedFromCurrentName = buildSenderIdFromAgentName(
      props.form.basic_info.agent_display_name || '',
    );
    // If a custom sender id is already present, do not override it.
    const senderIdManuallyEdited = ref(
      !!props.form.basic_info.sender_id
      && props.form.basic_info.sender_id !== generatedFromCurrentName,
    );

    watch(
      () => props.form.basic_info.agent_display_name,
      (value) => {
        if (senderIdManuallyEdited.value) return;
        props.form.basic_info.sender_id = buildSenderIdFromAgentName(value || '');
      },
      { immediate: true },
    );

    const senderIdError = computed(() => {
      const value = props.form.basic_info.sender_id;
      if (!value) return null;
      if (!SENDER_ID_REGEX.test(value)) return 'invalidSenderId';
      return null;
    });

    const handleSenderIdManualInput = () => {
      senderIdManuallyEdited.value = true;
    };

    return {
      t,
      hostingRegionOptions,
      senderIdError,
      handleSenderIdManualInput,
      hasReusableBrands,
      isCreatingNewBrand,
      brandSelection,
      brandOptions,
    };
  },
});
</script>
